import { assignCategoryColors } from "./mapLayerPalette";

const REQUIRED_COLUMNS = ["feature_id", "geom_type", "lat", "lon", "name"];
const NUMERIC_COLUMNS = ["sequence", "lat", "lon", "value"];

// Minimal RFC4180-ish line splitter — handles quoted fields, escaped quotes
// ("") and commas inside quotes.1
const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      pushField();
    } else if (c === "\n") {
      if (field !== "" || row.length) pushRow();
    } else if (c !== "\r") {
      field += c;
    }
  }
  if (field !== "" || row.length) pushRow();
  return rows;
};

const toRecord = (headers, cells) =>
  headers.reduce((record, header, index) => {
    const raw = cells[index] != null ? cells[index].trim() : "";
    record[header] = NUMERIC_COLUMNS.includes(header) && raw !== "" ? Number(raw) : raw || null;
    return record;
  }, {});

const buildFeature = (featureId, records) => {
  const ordered = [...records].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const first = ordered[0];
  const base = {
    featureId,
    geomType: first.geom_type === "line" ? "line" : "point",
    name: first.name,
    category: first.category || null,
    categorySecondary: first.category_secondary || null,
    value: first.value != null && !Number.isNaN(first.value) ? first.value : null,
    valueLabel: first.value_label || null,
    status: first.status || null,
    owner: first.owner || null,
    location: first.location || null,
    date: first.date || null,
    color: first.color || null,
    style: first.style === "dashed" ? "dashed" : "solid",
    notes: first.notes || null,
  };
  if (base.geomType === "line") {
    return { ...base, points: ordered.map((r) => [r.lat, r.lon]) };
  }
  return { ...base, lat: first.lat, lon: first.lon };
};

const computeValueDomain = (features) => {
  const values = features.map((f) => f.value).filter((v) => v != null && !Number.isNaN(v));
  if (!values.length) return null;
  const withLabel = features.find((f) => f.valueLabel);
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    label: withLabel ? withLabel.valueLabel : null,
  };
};

// Parses a "map layer" CSV (schema: feature_id, geom_type, sequence, lat, lon,
// name, category, category_secondary, value, value_label, status, owner,
// location, date, color, style, notes) into a layer object to append to map_layers on a case.
const parseMapLayerCsv = (text, { id, name } = {}) => {
  const rows = parseCsvRows(text.replace(/^\uFEFF/, ""));
  if (!rows.length) throw new Error("El archivo CSV está vacío.");

  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
  if (missing.length) {
    throw new Error(`Faltan columnas obligatorias en el CSV: ${missing.join(", ")}`);
  }

  const records = rows
    .slice(1)
    .filter((cells) => cells.some((c) => c !== ""))
    .map((cells) => toRecord(headers, cells));

  const byFeature = records.reduce((acc, record) => {
    const key = record.feature_id;
    if (!key) throw new Error("Hay filas sin feature_id.");
    if (!acc[key]) acc[key] = [];
    acc[key].push(record);
    return acc;
  }, {});

  const features = Object.entries(byFeature).map(([featureId, featureRecords]) =>
    buildFeature(featureId, featureRecords)
  );

  const categories = assignCategoryColors(features.map((f) => f.category));
  const categoriesWithSecondary = features.filter((f) => f.categorySecondary);
  const categoriesSecondary = categoriesWithSecondary.length
    ? assignCategoryColors(categoriesWithSecondary.map((f) => f.categorySecondary))
    : null;

  return {
    id: id || `layer-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
    name: name || "Untitled layer",
    visible: true,
    features,
    categories,
    categoriesSecondary,
    valueDomain: computeValueDomain(features),
  };
};

export { parseMapLayerCsv, parseCsvRows };
